(async () => {
  if (!customElements.get('product-media-slider')) {
    const {
      default: Swiper,
      Navigation,
      Pagination,
      Scrollbar,
      Autoplay,
      Thumbs,
    } = await import('swiper');

    const { Fancybox } = await import('@fancyapps/ui');

    Swiper.use([Navigation, Pagination, Scrollbar, Autoplay, Thumbs]);

    class ProductMediaSlider extends HTMLElement {
      constructor() {
        super();

        // Create elements
        this.thumbSlider();
        this.mainSlider();
        this.fancybox();
        this.resizeThumbSlider();

        // Listen for variant change and go to slide
        document.addEventListener('variant-change', (e) => this.slideToVariant(e));
      }

      connectedCallback() {
        window.addEventListener('resize', () => {
          this.resizeThumbSlider();
        });
      }

      /*
       * Create thumb slider
       */
      thumbSlider() {
        // Check if Swiper exists
        if (!Swiper) {
          return false;
        }

        // Get Thumbnails slider element & Set swiper options
        this.swiperThumbs = this.querySelector('[data-product-media-thumbnails-slider]');
        this.swiperThumbsOptions = {
          threshold: 10,
          spaceBetween: 8,
          direction: 'horizontal',
          slidesPerView: 6,
          watchSlidesProgress: true,
          watchOverflow: true,
          breakpoints: {
            768: {
              direction: 'vertical',
              slidesPerView: 'auto',
            },
          },
          navigation: {
            nextEl: '.swiper-button-thumbnails-next',
            prevEl: '.swiper-button-thumbnails-prev',
          },
        };

        // Create Thumbnails slider
        this.swiperThumbsInstance = new Swiper(this.swiperThumbs, this.swiperThumbsOptions);

        // Rebuild Swiper in design mode after delay
        if (Shopify.designMode && this.swiperThumbsInstance) {
          window.addEventListener('shopify:section:load', () => {
            setTimeout(() => {
              this.swiperThumbsInstance.update();
            }, 300);
          });
        }
      }

      /*
       * Create main slider
       */
      mainSlider() {
        // Check if Swiper exists
        if (typeof Swiper == 'undefined') {
          return false;
        }

        // Get swiper element
        this.swiperMain = this.querySelector('[data-product-media-slider]');

        // Get thumb options
        let thumbOptions;
        if (this.swiperThumbsInstance) {
          thumbOptions = {
            thumbs: {
              swiper: this.swiperThumbsInstance,
              slideThumbActiveClass: 'swiper-slide-thumb-active border-primary',
            },
          };
        }

        // Get initial slide and swiper index, or the previous saved slide index
        window.mainSliderIndex = window.mainSliderIndex || 0;
        if (window.mainSliderIndex >= this.swiperMain.querySelectorAll('.swiper-slide').length) {
          window.mainSliderIndex = this.swiperMain.querySelectorAll('.swiper-slide').length - 1;
        }

        // Swiper options
        this.swiperMainOptions = {
          threshold: 10,
          direction: 'horizontal',
          initialSlide: window.mainSliderIndex,
          spaceBetween: 4,
          slidesPerView: 1,
          navigation: {
            nextEl: '.swiper-button-next',
            prevEl: '.swiper-button-prev',
          },
          pagination: {
            el: '.media-pagination',
            type: 'bullets',
            clickable: true,
          },
          ...thumbOptions,
        };

        // Create Main slider
        this.swiperMainInstance = new Swiper(this.swiperMain, this.swiperMainOptions);

        // Save the main slider index when slide changes
        this.swiperMainInstance.on('slideChange', () => {
          window.mainSliderIndex = this.swiperMainInstance.activeIndex;
        });

        // Rebuild Swiper in design mode
        if (Shopify.designMode && this.swiperMainInstance) {
          window.addEventListener('shopify:section:load', () => {
            this.swiperMainInstance.update();
            this.resizeThumbSlider();
          });
        }
      }

      /*
       * Create fancybox
       */
      fancybox() {
        // Check if Fancybox exists
        if (typeof Swiper == 'undefined') {
          return false;
        }

        Fancybox.bind('[data-fancybox]', {
          animated: false,
          showClass: false,
          mainClass: 'image-zoom',
        });
      }

      /*
       * Slide to variant
       * @Param e {object}: event object with variant
       */
      slideToVariant(e) {
        if (e?.detail?.variant?.id) {
          const slideIndex = this.matchVariantIdWithSlide(e.detail.variant.id);
          if (typeof slideIndex == 'number') {
            this.goToSlide(slideIndex);
          }
        }
      }

      /*
       * Match variant id with slide
       * @param id {int}: variant id
       */
      matchVariantIdWithSlide(id) {
        if (!id || !this.swiperMain) {
          return false;
        }

        // Get slides
        const slides = this.swiperMain.querySelectorAll('[data-product-variant-image]');

        // Match id with slides id's
        const slide = [...slides].find((slide) => {
          // Get variant id's attached to slide
          const slideVariants = parseJson(slide.dataset.productVariantImage);

          // Check if variant id match the slide variant id's
          if (slideVariants.includes(id)) {
            return slide;
          }
        });

        // return false if no slide found
        if (!slide) {
          return false;
        }

        // return matched slide index
        return getNodeIndex(slide);
      }

      /*
       * go to slide
       * @param slide {node} / {int}: slide node / index
       */
      goToSlide(slide) {
        if ((typeof slide != 'number' && typeof slide != 'object') || !this.swiperMainInstance) {
          return false;
        }

        // Get index, transform node to slide index or string to int
        const index = typeof slide == 'number' ? parseInt(slide) : getNodeIndex(slide);

        // go to slide
        this.swiperMainInstance.slideTo(index);
      }

      resizeThumbSlider() {
        this.thumbs = this.querySelector('[data-product-media-thumbnails-slider]');
        if (window.innerWidth > 768) {
          this.mainSliderHeight = this.querySelector('[data-product-media-slider]').offsetHeight;

          this.thumbs.style.height = `${this.mainSliderHeight}px`;
        } else {
          this.thumbs.style.height = 'auto';
          this.swiperThumbsInstance.update();
        }
      }
    }

    window.ProductMediaSlider = ProductMediaSlider;

    customElements.define('product-media-slider', ProductMediaSlider);
  }
})();
